import { Block, Flex, Typography } from '@aloft/toolkit-sui';
import { Progress, Spinner } from '@aloft/toolkit-sui/chakra';

import { BulkCompResult } from './bulkAnalysisTypes.ts';

type Props = {
  results: BulkCompResult[];
  total: number;
  isRunning: boolean;
};

type AccuracyStat = { matches: number; total: number };

const AccuracyChip = ({ label, stat, isLoading }: { label: string; stat: AccuracyStat; isLoading: boolean }) => {
  const pct = stat.total === 0 ? null : Math.round((stat.matches / stat.total) * 100);
  const color =
    pct == null ? 'textSecondary' : pct >= 80 ? 'textSuccess' : pct >= 60 ? 'textWarning' : 'textError';

  return (
    <Block padding="12px 20px" borderRadius="8px" backgroundColor="surface2" minWidth="180px">
      <Typography variant="body2" color="textSecondary">
        {label}
      </Typography>
      {isLoading ? (
        <div style={{ marginTop: '6px' }}>
          <Spinner size="sm" />
        </div>
      ) : (
        <>
          <Typography variant="h2" color={color}>
            {pct == null ? '—' : `${pct}%`}
          </Typography>
          {stat.total > 0 && (
            <Typography variant="body2" color="textSecondary">
              {stat.matches} / {stat.total} exact match
            </Typography>
          )}
        </>
      )}
    </Block>
  );
};

const InfoChip = ({
  label,
  value,
  isLoading,
}: {
  label: string;
  value: string;
  isLoading: boolean;
}) => (
  <Block padding="12px 20px" borderRadius="8px" backgroundColor="surface2" minWidth="180px">
    <Typography variant="body2" color="textSecondary">
      {label}
    </Typography>
    {isLoading ? (
      <div style={{ marginTop: '6px' }}>
        <Spinner size="sm" />
      </div>
    ) : (
      <Typography variant="h2">{value}</Typography>
    )}
  </Block>
);

export const BulkResultsSummary = ({ results, total, isRunning }: Props) => {
  const done = results.filter((r) => r.status !== 'pending').length;

  const qualityStat: AccuracyStat = { matches: 0, total: 0 };
  const conditionStat: AccuracyStat = { matches: 0, total: 0 };
  let totalTokens = 0;
  let tokenCount = 0;
  let totalTimeMs = 0;
  let timeCount = 0;

  for (const r of results) {
    if (r.status !== 'success') continue;

    if (r.aiQuality != null) {
      qualityStat.total++;
      if (r.aiQuality === r.comp.uadQuality) qualityStat.matches++;
    }

    if (r.aiCondition != null && r.comp.uadCondition != null) {
      conditionStat.total++;
      if (r.aiCondition === r.comp.uadCondition) conditionStat.matches++;
    }

    const tokens = r.fullResult?._debug?.tokenUsage?.totalTokenCount;
    if (tokens != null) {
      totalTokens += tokens;
      tokenCount++;
    }

    if (r.timingMs != null) {
      totalTimeMs += r.timingMs;
      timeCount++;
    }
  }

  const avgTokens = tokenCount > 0 ? Math.round(totalTokens / tokenCount) : null;
  const avgTimeMs = timeCount > 0 ? Math.round(totalTimeMs / timeCount) : null;

  return (
    <Flex flexDirection="column" gap="16px">
      {(isRunning || done < total) && (
        <Flex flexDirection="column" gap="8px">
          <Flex justifyContent="space-between" alignItems="center">
            <Flex gap="8px" alignItems="center">
              <Spinner size="sm" />
              <Typography variant="body2" color="textSecondary">
                Analyzing comps...
              </Typography>
            </Flex>
            <Typography variant="body2" color="textSecondary">
              {done} / {total}
            </Typography>
          </Flex>
          <Progress
            value={total === 0 ? 0 : (done / total) * 100}
            size="sm"
            colorScheme="blue"
            borderRadius="4px"
          />
        </Flex>
      )}

      <Flex gap="16px" flexWrap="wrap">
        <AccuracyChip label="Quality accuracy" stat={qualityStat} isLoading={isRunning} />
        <AccuracyChip label="Condition accuracy" stat={conditionStat} isLoading={isRunning} />
        <InfoChip
          label="Total tokens"
          value={totalTokens.toLocaleString()}
          isLoading={isRunning}
        />
        <InfoChip
          label="Avg tokens / comp"
          value={avgTokens != null ? avgTokens.toLocaleString() : '—'}
          isLoading={isRunning}
        />
        <InfoChip
          label="Avg time / comp"
          value={avgTimeMs != null ? `${(avgTimeMs / 1000).toFixed(1)}s` : '—'}
          isLoading={isRunning}
        />
      </Flex>
    </Flex>
  );
};
